// application/coverageUnitOfWork.js
import { createCoverage } from '../domain/model/Coverage';
import { applyVerdict } from '../domain/model/EligibilityVerification';
import { BILLING_INTENT, INTENT } from './coverageChange';
import { partitionByLifecycle } from '../domain/services/coverageGroups';
import {
  applyOrder,
  compactRanks,
  moveByOffset,
  nextCobRank,
  reservedRanksOf,
  sortByPriority,
} from '../domain/services/coveragePriority';

/**
 * Unit of Work over a client's coverages.
 *
 * The client dialog stages every tab's edits and flushes them when the client is saved, so
 * coverage does the same rather than committing behind the user's back mid-dialog. Modeling
 * that as a Unit of Work — an ordered working set plus a record of intents — keeps the staging
 * concern in the application layer: the domain stays pure, and the UI never hand-rolls
 * create/update/delete bookkeeping.
 *
 * Staging is also what lets coverage be entered for a client that does not exist yet: intents
 * carry no client id until `commitCoverages` stamps the real one after the client is created.
 *
 * Reordering is why the intent set cannot be derived by diffing at flush time — promoting one
 * coverage renumbers its siblings, and each of those is a write.
 *
 * Only the in-sequence group participates in the ordering. Ended coverages are held aside with the
 * positions they had when they were live, so a past date of service still resolves the payer that was
 * actually primary at the time; they are never renumbered by a reorder happening today.
 *
 * Held aside is not the same as out of the way: an ended coverage and a live one can both be active
 * on a past service date, so their ranks are still compared against each other. Every ranking call
 * below therefore passes the ended group's ranks in as RESERVED, and the sequence is renumbered
 * around them. Reranking the sequence to 1..n on its own is what would let a live coverage take a
 * rank an ended one still holds — see the note in `coveragePriority`.
 */

/**
 * Ended coverages, most recently ended first — the order a biller scans when reconciling an old
 * date of service. A display ordering rather than a rule, so it is shaped here on the way out
 * instead of in the domain, which has no opinion about how an inert group is listed.
 */
const byMostRecentlyEnded = (a, b) => {
  const aEnd = a.period.end ?? '';
  const bEnd = b.period.end ?? '';
  return aEnd === bEnd ? `${a.id}`.localeCompare(`${b.id}`) : bEnd.localeCompare(aEnd);
};

/**
 * @typedef {import('../domain/model/Coverage.types').Coverage} Coverage
 * @typedef {import('./coverageChange').CoverageChange} CoverageChange
 */

/**
 * @typedef {Object} CoverageUnitOfWork
 *
 * The staged working set for one client's coverages: an ordered list plus a record of what has
 * to be written. `commitCoverages` is the only thing that turns it into writes.
 *
 * @property {(loaded: Coverage[]) => void} load - hydrate, keeping edits already staged
 * @property {() => Coverage[]} inSequence - the billing sequence, in order; excludes ended
 * @property {() => Coverage[]} ended - past their effective end, most recently ended first
 * @property {() => Coverage[]} all - including tombstones, for sibling validation
 * @property {() => boolean} hasPendingChanges
 * @property {() => boolean} hasReordered - the sequence differs from the order it was loaded in;
 *   ranks shifting because something was added or removed does not count
 * @property {(attrs: Object) => Coverage} add - stages a new coverage from flat form attributes,
 *   placed last
 * @property {(edited: Object) => void} update - stages an edit from flat form attributes;
 *   position is not the form's
 * @property {(id: string, verified: boolean) => void} setEligibilityVerified - records a verdict
 *   against an existing coverage, keeping the timestamp when the verdict is unchanged
 * @property {(id: string) => void} remove - stages a removal and closes the position gap
 * @property {(orderedIds: string[]) => void} reorder
 * @property {(id: string, offset: number) => void} move - -1 bills earlier, +1 later
 * @property {() => void} restoreLoadedOrder - put the sequence back the way it was loaded
 * @property {(openIntents: Array<{ id: string, coverageId: string }>) => void} loadPaused
 * @property {() => Set<string>} paused - coverage ids with billing paused, staged edits included
 * @property {(id: string, paused: boolean) => void} setBillingPaused
 * @property {() => import('./coverageChange').BillingIntentChange[]} billingIntentChanges
 * @property {() => CoverageChange[]} changes - the staged intents, ready to flush
 * @property {() => void} markCommitted - clears the intent log after a successful flush
 */

/**
 * @param {Object} deps
 * @param {() => string} deps.newId - generates an id for a staged coverage; it becomes the
 *   document id on flush, so a staged row keeps one identity for its whole life.
 * @param {() => string} deps.newIntentId - the same, for a staged billing block. A separate
 *   generator because the two live in different collections; the values would be
 *   indistinguishable either way, but nothing here should have to know that.
 * @param {string} deps.asOf - 'YYYY-MM-DD' the in-sequence/ended split is evaluated against.
 *   Fixed for the editing session so rows cannot jump groups mid-edit.
 * @returns {CoverageUnitOfWork}
 */
// eslint-disable-next-line import/prefer-default-export
export function createCoverageUnitOfWork({ newId, newIntentId, asOf }) {
  /** @type {import('../domain/model/Coverage.types').Coverage[]} */
  let coverages = [];
  /** @type {Map<string, string>} */
  const intents = new Map();
  /** Ids in the order they were loaded in — what "reordered" is measured against. */
  let baselineOrder = [];
  /**
   * Coverage id → id of the OPEN billing intent against it, as loaded. The intent's own id is
   * needed, not just the coverage's: releasing closes a specific record, and a coverage may carry
   * closed ones from earlier decisions.
   *
   * @type {Map<string, string>}
   */
  let openIntentByCoverage = new Map();
  /**
   * Pauses and releases staged this session, held separately from the loaded state so the flush is
   * a plain read of what was decided rather than a diff.
   *
   * The pause's id is generated HERE, when the user decides — not while building the change list.
   * Generating it there would make the change list impure: `hasPendingChanges` reads it on every
   * sync, so a fresh id would be minted several times a second and the one that reached Firestore
   * would be whichever call happened to be last.
   *
   * @type {Map<string, string>} coverage id → new intent id
   */
  let stagedPauses = new Map();
  /** @type {Set<string>} coverage ids whose loaded open intent is to be closed */
  let stagedReleases = new Set();

  const markDirty = (id, intent) => {
    // A row created in this session stays a create no matter how often it is edited after.
    if (intents.get(id) === INTENT.CREATE && intent === INTENT.UPDATE) {
      return;
    }
    intents.set(id, intent);
  };

  const groups = () => partitionByLifecycle(coverages, asOf);

  /** The ranks the ended group is holding; the sequence is renumbered around them, never over them. */
  const reservedRanks = () => reservedRanksOf(groups().ended);

  /** Replaces the in-sequence group with a reranked list, marking every rank that actually moved. */
  const applyRanked = (reranked) => {
    const previousRankById = new Map(coverages.map((coverage) => [coverage.id, coverage.cobRank]));

    reranked.forEach((coverage) => {
      if (previousRankById.get(coverage.id) !== coverage.cobRank) {
        markDirty(coverage.id, INTENT.UPDATE);
      }
    });

    const removed = coverages.filter((coverage) => coverage.isDeleted === true);
    coverages = [...reranked, ...groups().ended, ...removed];
  };

  const currentOrder = () => sortByPriority(groups().inSequence).map((coverage) => coverage.id);

  return {
    /**
     * Hydrates persisted coverages, keeping any edits already staged in this session — the tab
     * loads lazily, so a reload can land after the user has started editing.
     *
     * @param {import('../domain/model/Coverage.types').Coverage[]} loaded
     */
    load(loaded) {
      const staged = coverages.filter((coverage) => intents.has(coverage.id));
      const stagedIds = new Set(staged.map((coverage) => coverage.id));

      coverages = [
        ...staged,
        ...loaded.filter((coverage) => !stagedIds.has(coverage.id)),
      ];

      baselineOrder = sortByPriority(loaded).map((coverage) => coverage.id);
    },

    /**
     * Hydrates the open pauses, keeping any staged in this session — same late-reload concern as
     * `load`, and the staged decision is the one the user can see.
     *
     * @param {Array<{ id: string, coverageId: string }>} openIntents - the blocking ones
     */
    loadPaused(openIntents) {
      openIntentByCoverage = new Map(openIntents.map(({ id, coverageId }) => [coverageId, id]));
      // Staged decisions survive untouched — they are what the user can see, and a late reload
      // must not quietly undo one. A release staged against an intent that has since disappeared
      // is dropped, because there is nothing left to close.
      stagedReleases = new Set([...stagedReleases].filter((id) => openIntentByCoverage.has(id)));
      stagedPauses = new Map(
        [...stagedPauses].filter(([coverageId]) => !openIntentByCoverage.has(coverageId)),
      );
    },

    /** Coverage ids with billing paused, staged edits included. */
    paused() {
      const open = [...openIntentByCoverage.keys()].filter((id) => !stagedReleases.has(id));

      return new Set([...open, ...stagedPauses.keys()]);
    },

    /**
     * Pauses or releases billing on a coverage.
     *
     * Idempotent, and self-canceling: pausing then releasing before the client is saved leaves
     * things as they were found, so nothing is written. A decision made and unmade is not one.
     *
     * @param {string} id
     * @param {boolean} paused
     */
    setBillingPaused(id, paused) {
      if (paused === true) {
        // Un-staging a release restores the ORIGINAL decision rather than starting a new one —
        // the user changed their mind before saving, so nothing happened.
        if (stagedReleases.delete(id)) {
          return;
        }
        if (!openIntentByCoverage.has(id) && !stagedPauses.has(id)) {
          stagedPauses.set(id, newIntentId());
        }
        return;
      }

      if (stagedPauses.delete(id)) {
        return;
      }
      if (openIntentByCoverage.has(id)) {
        stagedReleases.add(id);
      }
    },

    /** The coordination-of-benefits sequence, in billing order. Excludes ended coverages. */
    inSequence() {
      return sortByPriority(groups().inSequence);
    },

    /** Coverages past their end date — retained, but out of the sequence. */
    ended() {
      return groups().ended.slice().sort(byMostRecentlyEnded);
    },

    /** Every coverage including tombstones — for validating against siblings. */
    all() {
      return coverages.slice();
    },

    hasPendingChanges() {
      return intents.size > 0 || this.billingIntentChanges().length > 0;
    },

    /**
     * Does the sequence currently differ from the order it was loaded in?
     *
     * A comparison, not a latch: dragging a row and dragging it back leaves the order as it was
     * found, so nothing has been reordered and the UI should stop saying so.
     *
     * Compared over the ids common to both, which is what excludes the two cases that shift ranks
     * without being a decision about precedence: a removal renumbers the survivors but leaves them
     * in the same relative order, and an addition appends an id the baseline has never seen.
     */
    hasReordered() {
      const current = currentOrder();
      const baseline = baselineOrder.filter((id) => current.includes(id));
      const comparable = current.filter((id) => baseline.includes(id));

      return comparable.join('|') !== baseline.join('|');
    },

    /**
     * Stages a new coverage, last in billing order so adding never silently changes which
     * payer is billed first.
     *
     * @param {Partial<import('../domain/model/Coverage.types').Coverage>} attrs
     * @returns {import('../domain/model/Coverage.types').Coverage}
     */
    add(attrs) {
      const coverage = createCoverage({
        ...attrs,
        id: newId(),
        cobRank: nextCobRank(groups().inSequence, reservedRanks()),
      });

      coverages = [...coverages, coverage];
      markDirty(coverage.id, INTENT.CREATE);

      return coverage;
    },

    /**
     * Stages an edit from the form. The rank is owned by the ordering operations, not the edit
     * form, so it is preserved from the existing row.
     *
     * Takes the form's FLAT attributes, not a built entity — constructing the entity is this
     * layer's job, which is what keeps `createCoverage` single-shaped. To change a field on a
     * coverage that already exists, use `patch`.
     *
     * @param {{ id: string, effectiveStart?: *, effectiveEnd?: * } &
     *   Partial<import('../domain/model/Coverage.types').Coverage>} edited
     */
    update(edited) {
      const existing = coverages.find((coverage) => coverage.id === edited.id);
      if (existing === undefined) {
        return;
      }

      const merged = createCoverage({ ...edited, cobRank: existing.cobRank });
      coverages = coverages.map((coverage) => (coverage.id === merged.id ? merged : coverage));
      markDirty(merged.id, INTENT.UPDATE);

      // An edit can change group membership — setting an end date in the past drops a coverage
      // out of the sequence — so close any rank gap that leaves behind. The coverage that just
      // left keeps its rank, and compaction renumbers around it rather than taking it back.
      applyRanked(compactRanks(groups().inSequence, reservedRanks()));
    },

    /**
     * Records an eligibility verdict against a coverage that already exists — the row toggle.
     *
     * Distinct from `update`, which rebuilds the entity from the form's flat input. A toggle has
     * no form behind it, so routing it through the factory would mean spreading a built aggregate
     * back in as if it were raw attributes — and the aggregate carries value objects where the
     * factory expects flat fields. It would find no `effectiveStart` and hand back a coverage that
     * never ends, and no `policyholderId` and hand back one pointing at the client. Both losses are
     * silent, which is why every non-form mutation spreads the entity instead.
     *
     * `applyVerdict` is what decides whether this is a NEW check or the same one restated, so an
     * unchanged verdict keeps its original timestamp.
     *
     * @param {string} id
     * @param {boolean} verified
     */
    setEligibilityVerified(id, verified) {
      const existing = coverages.find((coverage) => coverage.id === id);
      if (existing === undefined) {
        return;
      }

      const merged = {
        ...existing,
        eligibilityVerification: applyVerdict(existing.eligibilityVerification, verified),
      };
      coverages = coverages.map((coverage) => (coverage.id === id ? merged : coverage));
      markDirty(id, INTENT.UPDATE);
    },

    /**
     * Stages a removal and closes the position gap it leaves.
     *
     * The ONLY hard delete in the system is here: a row that was added in this session and never
     * reached Firestore is dropped outright, because there is no document to tombstone and no
     * history to keep. Anything that has been persisted is soft-deleted instead — the intent
     * becomes a DELETE, which the flush turns into `isDeleted: true`.
     *
     * @param {string} id
     */
    remove(id) {
      if (intents.get(id) === INTENT.CREATE) {
        intents.delete(id);
        coverages = coverages.filter((coverage) => coverage.id !== id);
      } else {
        if (!coverages.some((coverage) => coverage.id === id)) {
          return;
        }
        coverages = coverages.map((coverage) => {
          return coverage.id === id ? { ...coverage, isDeleted: true } : coverage;
        });
        markDirty(id, INTENT.DELETE);
      }

      // A removed coverage keeps no open pause: the decision not to bill it is subsumed by its no
      // longer being there, and an open intent would point at a tombstone.
      this.setBillingPaused(id, false);

      applyRanked(compactRanks(groups().inSequence, reservedRanks()));
    },

    /**
     * Stages a drag-reorder.
     * @param {string[]} orderedIds
     */
    reorder(orderedIds) {
      applyRanked(applyOrder(groups().inSequence, orderedIds, reservedRanks()));
    },

    /**
     * Stages a one-step move — the keyboard-accessible companion to dragging.
     * @param {string} id
     * @param {number} offset -1 to bill earlier, +1 to bill later
     */
    move(id, offset) {
      applyRanked(moveByOffset(groups().inSequence, id, offset, reservedRanks()));
    },

    /**
     * Puts the sequence back the way it was loaded, undoing a session's worth of dragging.
     *
     * Every awkward case falls out of `applyOrder`'s existing rules rather than needing its own
     * branch: a coverage removed or ended since load is no longer in the sequence and its id is
     * ignored, and one added since load is not in the baseline at all, so it keeps its place at
     * the end — which is where adding put it. Only what was dragged moves back.
     */
    restoreLoadedOrder() {
      applyRanked(applyOrder(groups().inSequence, baselineOrder, reservedRanks()));
    },

    /**
     * The staged intents, ready to flush.
     * @returns {Array<{ intent: string, coverage: import('../domain/model/Coverage.types').Coverage }>}
     */
    changes() {
      return [...intents.entries()]
        .map(([id, intent]) => {
          const coverage = coverages.find((candidate) => candidate.id === id);
          return coverage === undefined ? null : { intent, coverage };
        })
        .filter((change) => change !== null);
    },

    /**
     * Pause decisions to write, derived by diffing against the loaded set.
     *
     * The id is the coverage's own, not a separate one: at most one live intent may exist per
     * coverage, so the coverage id identifies the decision uniquely and a release can find it
     * without a lookup. `clientId`/`clinicId` are stamped by `commitCoverages`, the same way
     * coverage changes are, because a coverage can be staged before its client exists.
     *
     * @returns {import('./coverageChange').BillingIntentChange[]}
     */
    billingIntentChanges() {
      const blank = (id, coverageId) => ({
        id,
        clientId: null,
        clinicId: null,
        coverageId,
        reasonType: null,
        isBlocking: true,
        blockedBy: null,
        releaseTimestamp: null,
        releasedBy: null,
        isDeleted: false,
      });

      return [
        // A pause is a NEW record — never a resurrection of a closed one, because pausing again is
        // a different decision, possibly by a different person, and the earlier one still happened.
        ...[...stagedPauses].map(([coverageId, intentId]) => ({
          verb: BILLING_INTENT.PAUSE,
          billingIntent: blank(intentId, coverageId),
        })),
        // A release closes the OPEN intent, by its own id. It does not delete it.
        ...[...stagedReleases].map((coverageId) => ({
          verb: BILLING_INTENT.RELEASE,
          billingIntent: blank(openIntentByCoverage.get(coverageId), coverageId),
        })),
      ];
    },

    /** Clears the intent log after a successful flush. */
    markCommitted() {
      intents.clear();
      coverages = coverages.filter((coverage) => coverage.isDeleted !== true);
      // What was staged is now what is stored, so it becomes the order to measure against.
      baselineOrder = currentOrder();
      // What was staged is now what is stored: newly paused coverages carry their new intent id,
      // and released ones no longer have an open intent at all.
      stagedReleases.forEach((coverageId) => openIntentByCoverage.delete(coverageId));
      stagedPauses.forEach((intentId, coverageId) => openIntentByCoverage.set(coverageId, intentId));
      stagedPauses = new Map();
      stagedReleases = new Set();
    },
  };
} // createCoverageUnitOfWork()
